import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

interface Props {
  images: string[];
  startIndex?: number;
  onClose: () => void;
}

export function Lightbox({ images, startIndex = 0, onClose }: Props) {
  const [index, setIndex] = useState(startIndex);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") setIndex((i) => (i + 1) % images.length);
      if (e.key === "ArrowLeft") setIndex((i) => (i - 1 + images.length) % images.length);
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [images.length, onClose]);

  if (images.length === 0) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" role="dialog" aria-modal="true">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="fixed inset-0 bg-overlay backdrop-blur-[2px]"
      />
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute right-3 top-3 z-10 flex size-9 items-center justify-center rounded-full bg-surface/90 text-fg shadow-[var(--shadow-2)] [&_svg]:size-5"
      >
        <X />
      </button>

      {images.length > 1 && (
        <button
          type="button"
          aria-label="Previous image"
          onClick={() => setIndex((i) => (i - 1 + images.length) % images.length)}
          className="absolute left-3 z-10 flex size-10 items-center justify-center rounded-full bg-surface/90 text-fg shadow-[var(--shadow-2)] [&_svg]:size-5"
        >
          <ChevronLeft />
        </button>
      )}

      <img
        src={images[index]}
        alt=""
        className="relative max-h-[88vh] max-w-[92vw] rounded-[var(--radius-md)] object-contain shadow-[var(--shadow-4)]"
      />

      {images.length > 1 && (
        <>
          <button
            type="button"
            aria-label="Next image"
            onClick={() => setIndex((i) => (i + 1) % images.length)}
            className="absolute right-3 z-10 flex size-10 items-center justify-center rounded-full bg-surface/90 text-fg shadow-[var(--shadow-2)] [&_svg]:size-5"
          >
            <ChevronRight />
          </button>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-surface/90 px-2.5 py-1 text-xs text-fg-muted tabular shadow-[var(--shadow-1)]">
            {index + 1} / {images.length}
          </div>
        </>
      )}
    </div>
  );
}
